import { useMemo, useState } from 'react';
import { Alert, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Stack, TextField, Typography } from '@mui/material';
import { DialogCommonFields } from './DialogCommonFields';
import { buildFromSchedule, countSourceDays, DEFAULT_FLEXIBLE_START, hasFlexibleShift, hhmmToMinutes, scheduleEmptyReason, ScheduleEmptyReason } from './punchPlan';
import { useCreatePunches } from './useCreatePunches';
import { CreatedBy, CreatePunchesRequest, CreatePunchesResponse, DayDataContext, DayEntry, MAX_PUNCHES_PER_REQUEST } from '../types';
import { UUID_RE } from '../urlParams';

/** One-line explanation shown under the preview count when buildFromSchedule would produce zero punches, keyed by scheduleEmptyReason's result. */
export const SCHEDULE_EMPTY_HINTS: Record<ScheduleEmptyReason, string> = {
  'no-days': 'No days loaded. Load data for a date range first.',
  'all-days-have-punches': 'Every loaded day already has punches. Turn off "Skip days with punches" to add more.',
  'no-working-shifts': 'No working shifts in the loaded days. Pick a range with scheduled working days.',
  'only-breaks-excluded': 'The shifts only have break events. Turn on "Include breaks" to create them.',
  'no-fixed-times': 'These flexible shifts have no working duration, so nothing can be placed. Use "Add custom punches" for these days.',
  'bad-flexible-start': 'Enter a valid start time (HH:MM) for the flexible shifts.'
};

const REQUESTED_BY_INVALID_HINT = 'Fix the Admin profile UUID first.';

interface AddFromScheduleDialogProps {
  open: boolean;
  days: DayEntry[];
  context: DayDataContext;
  onClose: () => void;
  onCreate: (request: CreatePunchesRequest) => Promise<CreatePunchesResponse>;
}

export const AddFromScheduleDialog = ({ open, days, context, onClose, onCreate }: AddFromScheduleDialogProps) => {
  const [skipDaysWithPunches, setSkipDaysWithPunches] = useState(true);
  const [includeBreaks, setIncludeBreaks] = useState(true);
  const [entryOffset, setEntryOffset] = useState(0);
  const [breakStartOffset, setBreakStartOffset] = useState(0);
  const [breakEndOffset, setBreakEndOffset] = useState(0);
  const [exitOffset, setExitOffset] = useState(0);
  const [flexibleStart, setFlexibleStart] = useState(DEFAULT_FLEXIBLE_START);
  const [createdBy, setCreatedBy] = useState<CreatedBy>('admin');
  const [requestedBy, setRequestedBy] = useState('');
  const [reason, setReason] = useState('');
  const requestedByInvalid = requestedBy !== '' && !UUID_RE.test(requestedBy);
  const { submitting, error, partial, submit } = useCreatePunches(onCreate, onClose);

  const planned = useMemo(
    () => buildFromSchedule(days, { skipDaysWithPunches, includeBreaks, entryOffset, breakStartOffset, breakEndOffset, exitOffset, flexibleStart }),
    [days, skipDaysWithPunches, includeBreaks, entryOffset, breakStartOffset, breakEndOffset, exitOffset, flexibleStart]
  );
  // Distinct SOURCE dates, not distinct shiftIds — a night shift's exit rolls onto the next
  // calendar date, which would otherwise double-count that one shift as two "days".
  const dayCount = useMemo(() => countSourceDays(days, { skipDaysWithPunches, includeBreaks }), [days, skipDaysWithPunches, includeBreaks]);
  const overCap = planned.length > MAX_PUNCHES_PER_REQUEST;
  // Why the preview would be empty (null when it would not be) — explains a disabled Create button to the tester.
  const emptyReason = useMemo(
    () => scheduleEmptyReason(days, { skipDaysWithPunches, includeBreaks, entryOffset, breakStartOffset, breakEndOffset, exitOffset, flexibleStart }),
    [days, skipDaysWithPunches, includeBreaks, entryOffset, breakStartOffset, breakEndOffset, exitOffset, flexibleStart]
  );

  const doSubmit = () => submit({ createdBy, requestedBy: requestedBy || undefined, reason: createdBy === 'employee_realtime' ? undefined : reason || undefined, punches: planned });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add punches from schedule</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControlLabel control={<Checkbox checked={skipDaysWithPunches} onChange={(e) => setSkipDaysWithPunches(e.target.checked)} />} label="Skip days with punches" />
          <FormControlLabel control={<Checkbox checked={includeBreaks} onChange={(e) => setIncludeBreaks(e.target.checked)} />} label="Include breaks" />
          <Stack direction="row" spacing={2}>
            <TextField label="Entry offset (min)" type="number" size="small" value={entryOffset} onChange={(e) => setEntryOffset(Number(e.target.value))} />
            <TextField label="Break start offset (min)" type="number" size="small" value={breakStartOffset} onChange={(e) => setBreakStartOffset(Number(e.target.value))} />
            <TextField label="Break end offset (min)" type="number" size="small" value={breakEndOffset} onChange={(e) => setBreakEndOffset(Number(e.target.value))} />
            <TextField label="Exit offset (min)" type="number" size="small" value={exitOffset} onChange={(e) => setExitOffset(Number(e.target.value))} />
          </Stack>
          {hasFlexibleShift(days) && (
            <TextField
              label="Flexible shift start"
              type="time"
              size="small"
              value={flexibleStart}
              onChange={(e) => setFlexibleStart(e.target.value)}
              helperText="Used for flexible shifts: entry at this time, exit after the shift's working minutes plus breaks."
              error={hhmmToMinutes(flexibleStart) === null}
            />
          )}
          <DialogCommonFields createdBy={createdBy} onCreatedByChange={setCreatedBy} reason={reason} onReasonChange={setReason} requestedBy={requestedBy} onRequestedByChange={setRequestedBy} context={context} />
          <Typography variant="body2">
            Will create {planned.length} punches on {dayCount} days
          </Typography>
          {overCap && (
            <Typography variant="body2" color="error">
              Maximum 200 punches per request
            </Typography>
          )}
          {!submitting && !overCap && requestedByInvalid && (
            <Typography variant="body2" color="warning.main">
              {REQUESTED_BY_INVALID_HINT}
            </Typography>
          )}
          {!submitting && !overCap && !requestedByInvalid && emptyReason && (
            <Typography variant="body2" color="warning.main">
              {SCHEDULE_EMPTY_HINTS[emptyReason]}
            </Typography>
          )}
          {error && <Alert severity="error">{error}</Alert>}
          {partial && (
            <Stack spacing={0.5}>
              <Typography variant="body2">
                Created {partial.created.length}, failed {partial.failed.length}
              </Typography>
              {partial.failed.map((f, i) => (
                <Typography key={i} variant="body2" color="error">
                  {f.date} {f.time} {f.punchType}: {f.error}
                </Typography>
              ))}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {partial ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="contained" disabled={planned.length === 0 || overCap || submitting || requestedByInvalid} startIcon={submitting ? <CircularProgress size={16} /> : undefined} onClick={doSubmit}>
              Create
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddFromScheduleDialog;
